import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateSession } from '../../../services/api';
import { AGENT_SESSIONS } from '../../../constants/queryKeys';
import type {
  SessionHistoryItemResponse,
  SessionHistoryResponse,
} from '../../../types';

type InfiniteSessions = {
  pages: SessionHistoryResponse[];
  pageParams: unknown[];
};

const PINNED_KEY = [AGENT_SESSIONS, 'pinned'];
const UNPINNED_KEY = [AGENT_SESSIONS, 'unpinned'];

const useTogglePin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionId,
      pinned,
    }: {
      sessionId: string;
      pinned: boolean;
    }) => updateSession(sessionId, { pinned }),
    onMutate: async ({ sessionId, pinned }) => {
      await queryClient.cancelQueries({ queryKey: [AGENT_SESSIONS] });
      const prevPinned = queryClient.getQueryData<InfiniteSessions>(PINNED_KEY);
      const prevUnpinned =
        queryClient.getQueryData<InfiniteSessions>(UNPINNED_KEY);

      const sourceKey = pinned ? UNPINNED_KEY : PINNED_KEY;
      const targetKey = pinned ? PINNED_KEY : UNPINNED_KEY;

      let moved: SessionHistoryItemResponse | undefined;
      const source = queryClient.getQueryData<InfiniteSessions>(sourceKey);
      if (source) {
        queryClient.setQueryData<InfiniteSessions>(sourceKey, {
          ...source,
          pages: source.pages.map((page) => ({
            ...page,
            sessions: page.sessions.filter((session) => {
              if (session.session_id === sessionId) {
                moved = session;
                return false;
              }
              return true;
            }),
          })),
        });
      }

      if (moved) {
        const updated: SessionHistoryItemResponse = {
          ...moved,
          pinned_at: pinned ? new Date().toISOString() : null,
        };
        queryClient.setQueryData<InfiniteSessions>(targetKey, (data) => {
          if (!data) {
            return {
              pages: [{ sessions: [updated] }],
              pageParams: [0],
            };
          }
          const [firstPage, ...restPages] = data.pages;
          return {
            ...data,
            pages: firstPage
              ? [
                  { ...firstPage, sessions: [updated, ...firstPage.sessions] },
                  ...restPages,
                ]
              : [{ sessions: [updated] }],
          };
        });
      }

      return { prevPinned, prevUnpinned };
    },
    onError: (_error, _variables, context) => {
      const restore = (
        key: typeof PINNED_KEY,
        prev: InfiniteSessions | undefined,
      ) => {
        if (prev) {
          queryClient.setQueryData(key, prev);
        } else {
          queryClient.removeQueries({ queryKey: key, exact: true });
        }
      };
      restore(PINNED_KEY, context?.prevPinned);
      restore(UNPINNED_KEY, context?.prevUnpinned);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: [AGENT_SESSIONS] });
    },
  });
};

export default useTogglePin;
